/**
 * Types for Containers Recap API responses
 *
 * These types match the backend Pydantic schemas in api/routers/containers_recap.py
 */

// ============================================================================
// TIME RANGE OPTIONS
// ============================================================================

export type TimeRangeOption = {
  value: number;
  label: string;
  labelKey: string; // i18n key
};

export const TIME_RANGE_OPTIONS: TimeRangeOption[] = [
  { value: 30, label: 'Mese corrente', labelKey: 'timeRange.currentMonth' },
  { value: 60, label: 'Ultimo mese', labelKey: 'timeRange.lastMonth' },
  { value: 90, label: 'Ultimi 3 mesi', labelKey: 'timeRange.last3Months' },
  { value: 180, label: 'Ultimi 6 mesi', labelKey: 'timeRange.last6Months' },
  { value: 270, label: 'Ultimi 9 mesi', labelKey: 'timeRange.last9Months' },
  { value: 365, label: 'Ultimo anno', labelKey: 'timeRange.lastYear' },
];

// ============================================================================
// MAP ENDPOINT TYPES
// ============================================================================

export type RiskLevel = 'low' | 'medium' | 'high' | 'critical';

export interface ContainerPosition {
  container_id: string;
  container_number: string;
  status: string;
  risk_level: RiskLevel;

  // Latest position from tracking event
  latitude: number | null;
  longitude: number | null;
  location_name: string | null;
  location_port_code: string | null;
  last_event_timestamp: string | null;

  // Route info for display
  origin_port_name: string | null;
  origin_country_code: string | null;
  origin_port_lat: number | null;
  origin_port_lon: number | null;
  destination_port_name: string | null;
  destination_country_code: string | null;
  destination_port_lat: number | null;
  destination_port_lon: number | null;
  carrier_name: string | null;
}

export interface ContainerMapResponse {
  containers: ContainerPosition[];
  total_count: number;
  start_date: string | null;
  end_date: string | null;
  as_of: string;
}

// ============================================================================
// STATS ENDPOINT TYPES
// ============================================================================

export interface DistributionItem {
  label: string;
  value: number;
  percentage: number;
}

export interface StatsRecapResponse {
  total_containers: number;
  start_date: string | null;
  end_date: string | null;

  // Risk level distribution (Low/Medium/High/Critical)
  risk_distribution: DistributionItem[];

  // Container status distribution
  status_distribution: DistributionItem[];

  // Document status distribution (Complete/Pending/Error)
  document_distribution: DistributionItem[];

  // Summary stats
  containers_with_issues: number;
  containers_with_complete_docs: number;

  // Additional aggregated stats for dashboard cards
  in_transit: number; // containers with status IN_TRANSIT
  delivered: number; // containers with status DELIVERED
  pending_documents: number; // containers with documents_complete = false
  high_risk: number; // containers with risk_score >= 0.50 (high or critical)

  // Issue counts (new — from container_issues aggregation)
  doc_issue_count?: number; // Active document validation issues (missing docs, check failures, cross-check errors)
  containers_with_doc_issues?: number; // Distinct containers with active doc issues
  total_issue_count?: number; // Total active issues across all containers

  // Granular document issue breakdown
  missing_document_count?: number;
  single_check_issue_count?: number;
  cross_check_issue_count?: number;
  non_doc_issue_count?: number; // total_issue_count - doc_issue_count

  /** BOOKED containers with ETD within the next 2 days (inclusive), scoped to filters */
  about_to_depart_count?: number;

  as_of: string;
}

// ============================================================================
// TABLE ENDPOINT TYPES
// ============================================================================

export interface ContainerTableRow {
  container_id: string;
  container_number: string;

  // Route
  origin_port_name: string | null;
  origin_country_code: string | null;
  /** UN/LOCODE (5 chars), e.g. KEMBA */
  origin_port_unlocode: string | null;
  destination_port_name: string | null;
  destination_country_code: string | null;
  destination_port_unlocode: string | null;

  // Carrier & Transport
  carrier_name: string | null;
  vessel_name: string | null;
  voyage_number: string | null;
  incoterms: string | null;
  cargo_type: string | null; // FCL, LCL, etc.
  transport_mode: string | null; // Sea, Air, Road, Rail, Multimodal

  // Progress (calculated from position/dates)
  progress_percentage: number;

  // Dates
  estimated_arrival_date: string | null;
  original_estimated_arrival_date: string | null;
  actual_arrival_date: string | null;
  estimated_departure_date: string | null;
  actual_departure_date: string | null;
  booking_date: string | null;
  estimated_delivery_date: string | null;
  created_at: string | null;
  updated_at: string | null;

  // Delay (days vs original ETA)
  delay_days: number | null;

  // Status
  status: string;
  risk_level: RiskLevel;

  // Documents
  documents_complete: boolean;
  documents_validated: boolean;
  document_count: number;

  // Emails
  email_count: number;

  // Booking & References
  booking_number: string | null;
  bl_number: string | null;
  purchase_order_number: string | null;
  service_contract_number: string | null;

  // Cargo Details
  is_hazardous: boolean;
  hazard_class: string | null;
  total_weight_kg: number | null;
  total_volume_cbm: number | null;
  total_packages: number | null;
  cargo_description: string | null;
  hs_code_primary: string | null;

  // Financial
  total_value_usd: number | null;
  freight_charges_usd: number | null;
  currency: string | null;
  insurance_value_usd: number | null;

  // Temperature Control
  requires_temperature_control: boolean;
  temperature_setpoint_celsius: number | null;

  // Tracking
  last_tracked_at: string | null;
  tracking_provider: string | null;
  alert_count: number;
  has_active_alerts: boolean;

  // Notes
  special_instructions: string | null;
  customer_notes: string | null;

  // Consignee info
  consignee_company_id: string | null;
  consignee_name: string | null;

  // Per-container status summaries
  document_status_overall: string; // "X docs need attention" or "All docs are OK"
  exogen_status_overall: string; // "X general problems" or "All systems OK"

  // Document issue details (new)
  docs_with_issues: number; // Count of docs with extraction_status != 'success'
  docs_pending: number; // Count of docs with extraction_status == 'pending'
  doc_status_message: string; // Formatted message for UI

  // Last update tracking (new)
  last_update_at: string | null; // Most recent update timestamp
  last_update_type: string | null; // "TrackUpdate", "DocumentUpload", "EmailUpdate", "IssueCreated"

  // Physical status (new)
  physical_status: string;

  // Sea route for map (only when include_sea_route=true)
  sea_route?: { coordinates: [number, number][]; distance_km: number };

  // Which date was used for date-range filtering: "eta" | "etd" | "booking_date" | "created_at"
  date_reference_source?: string | null;

  /** Latest tracking fix from container_events when coordinates exist */
  current_position?: {
    latitude: number;
    longitude: number;
    recorded_at: string;
    speed_knots?: number | null;
    course_degrees?: number | null;
    heading_degrees?: number | null;
    imo?: string | null;
    mmsi?: string | null;
    ais_destination?: string | null;
    ais_eta_utc?: string | null;
    position_fix_at?: string | null;
  } | null;
}

export interface ContainerTableResponse {
  containers: ContainerTableRow[];
  total_count: number;
  start_date: string | null;
  end_date: string | null;

  // Pagination metadata
  skip: number;
  limit: number;
  has_more: boolean;

  as_of: string;
}

// ============================================================================
// QUERY PARAMS TYPES
// ============================================================================

export interface ContainersRecapQueryParams {
  start_date?: string;
  end_date?: string;
}

export interface ContainerTableQueryParams extends ContainersRecapQueryParams {
  skip?: number;
  limit?: number;
  include_sea_route?: boolean; // Include sea route coordinates per container for map
  search?: string; // Search by container number, booking number, or B/L number
  status?: string;
  risk_level?: string; // comma-separated for multi-select: "low,medium,high"
  doc_status?: string; // "pending" | "complete"
  physical_status?: string; // comma-separated groups: "pre_departure,in_transit,at_destination,delivered,on_hold,cancelled,pending"
  shipment_status?: string; // comma-separated: "draft,booked,in_transit,arrived,delivered,on_hold,cancelled"
  carrier_scac?: string; // Filter by carrier SCAC code
  carrier_name?: string; // Filter by carrier name (exact match)
  has_doc_issues?: boolean; // Filter containers with doc issues
  has_active_alerts?: boolean; // Filter containers with active alerts (non-document issues)
  has_partial_data?: boolean; // Filter containers with partial data (missing carrier, receiver, ETA, ETD, origin or dest port)
  sort_by?: string; // "etd" | "eta" | "risk" | "progress" | "doc_issues" | "issues" | "status" | "last_update"
  sort_order?: string; // "asc" | "desc"
}

// Physical status groups for filtering
export type PhysicalStatusGroup =
  | "pre_departure"
  | "in_transit"
  | "at_destination"
  | "delivered"
  | "on_hold"
  | "cancelled"
  | "pending";

export const PHYSICAL_STATUS_GROUPS: {
  id: PhysicalStatusGroup | "";
  label: string;
  labelKey: string;
  description?: string;
}[] = [
  { id: "", label: "All Statuses", labelKey: "physicalStatus.all" },
  {
    id: "pre_departure",
    label: "Pre-Departure",
    labelKey: "physicalStatus.preDeparture",
    description: "Gate out, Loading, Gate in, On vessel",
  },
  {
    id: "in_transit",
    label: "In Transit",
    labelKey: "physicalStatus.inTransit",
    description: "Vessel departed, In transit",
  },
  {
    id: "at_destination",
    label: "At Destination",
    labelKey: "physicalStatus.atDestination",
    description: "Arrived, Discharged, Delivery gate out",
  },
  { id: "delivered", label: "Delivered", labelKey: "physicalStatus.delivered" },
  { id: "on_hold", label: "On Hold", labelKey: "physicalStatus.onHold" },
  { id: "cancelled", label: "Cancelled", labelKey: "physicalStatus.cancelled" },
  { id: "pending", label: "Pending", labelKey: "physicalStatus.pending" },
];

// Shipment status filter (matches ShipmentStatus enum on backend)
export type ShipmentStatusFilter =
  | "draft"
  | "booked"
  | "in_transit"
  | "arrived"
  | "delivered"
  | "on_hold"
  | "cancelled";

export const SHIPMENT_STATUS_OPTIONS: {
  id: ShipmentStatusFilter | "";
  label: string;
  labelKey: string;
}[] = [
  { id: "", label: "All Statuses", labelKey: "shipmentStatus.all" },
  { id: "draft", label: "Draft", labelKey: "shipmentStatus.draft" },
  { id: "booked", label: "Booked", labelKey: "shipmentStatus.booked" },
  { id: "in_transit", label: "In Transit", labelKey: "shipmentStatus.inTransit" },
  { id: "arrived", label: "Arrived", labelKey: "shipmentStatus.arrived" },
  { id: "delivered", label: "Delivered", labelKey: "shipmentStatus.delivered" },
  { id: "on_hold", label: "On Hold", labelKey: "shipmentStatus.onHold" },
  { id: "cancelled", label: "Cancelled", labelKey: "shipmentStatus.cancelled" },
];
